import { Injectable } from '@angular/core';
import { MessageService } from '../service-ui/message.service';
import { HttpClient } from '@angular/common/http';
import { StartupService } from '../service-ui/startup.service';
import {EcommerceConnection} from "../../model/EcommerceConnection";
import {AbstractLiveDataService} from "./abstract-live-data.service";
import {NotificationsService} from "./notifications.service";
import {ManufacturingTask} from "../../model/ManufacturingTask";
import {Observable, of} from "rxjs";
import {environment} from "../../environments/environment";
import {catchError, map} from "rxjs/operators";

@Injectable()
export class EcommerceConnectionService extends AbstractLiveDataService<EcommerceConnection> {

  constructor(private httpa: HttpClient, private messageServicea: MessageService, private startupServicea: StartupService, protected notificationsService: NotificationsService) {
    super(httpa, messageServicea, startupServicea, notificationsService);
    notificationsService.setEcommerceConnectionService(this);
    notificationsService.startNotification();
  }

  makeObjectInstance(args: any): EcommerceConnection {
    return new EcommerceConnection(args);
  }

  getLoggingObjectTypeName (): string {
    return "EcommerceConnection";
  }

  supportsParallel(): boolean {
    return true;
  }

  getURLEndPoint (): string {
    return "ecommerceconnection";
  }

  getLinkData(): any[] {
    return [
      { action: 'Link', objectIdPropertyName: 'org_ID', serviceName: 'organizationService', arrayProperty: 'ecommerceConnections', objectProperty: 'organization'},
    ];
  }
  checkConnection (ecommerceConnection: EcommerceConnection): Observable<EcommerceConnection> {
    const method = 'checkconnection';
    const url = `${environment.apiBaseURL}${this.getURLEndPoint()}/${method}/${ecommerceConnection.ID}`;
    this.messageService.add(`${this.getLoggingObjectTypeName()} ${method}: ecommerceConnectionId=${ecommerceConnection.ID}`);
    return this.http.put(url, {}, this.startupService.getHttpOptions())
      .pipe(
        map(changedEcommerceConnection => {
          const m = new EcommerceConnection(ecommerceConnection);
          return m;
        }),
        catchError(this.handleErrorT<any>(method))
      );
  }

  readOrders(ecommerceConnection: EcommerceConnection): Observable<EcommerceConnection> {
    const method = 'readorders';
    const url = `${environment.apiBaseURL}${this.getURLEndPoint()}/${method}/${ecommerceConnection.ID}`;
    this.messageService.add(`${this.getLoggingObjectTypeName()} ${method}: ecommerceConnectionId=${ecommerceConnection.ID}`);
    return this.http.put(url, {}, this.startupService.getHttpOptions())
      .pipe(
        map(changedEcommerceConnection => {
          const m = new EcommerceConnection(ecommerceConnection);
          return m;
        }),
        catchError(this.handleErrorT<any>(method))
      );
  }

  readProducts(ecommerceConnection: EcommerceConnection): Observable<EcommerceConnection> {
    const method = 'readproducts';
    const url = `${environment.apiBaseURL}${this.getURLEndPoint()}/${method}/${ecommerceConnection.ID}`;
    this.messageService.add(`${this.getLoggingObjectTypeName()} ${method}: ecommerceConnectionId=${ecommerceConnection.ID}`);
    return this.http.put(url, {}, this.startupService.getHttpOptions())
      .pipe(
        map(changedEcommerceConnection => {
          const m = new EcommerceConnection(ecommerceConnection);
          return m;
        }),
        catchError(this.handleErrorT<any>(method))
      );
  }

  processOrders(ecommerceConnection: EcommerceConnection): Observable<EcommerceConnection> {
    const method = 'processorders';
    const url = `${environment.apiBaseURL}${this.getURLEndPoint()}/${method}/${ecommerceConnection.ID}`;
    this.messageService.add(`${this.getLoggingObjectTypeName()} ${method}: ecommerceConnectionId=${ecommerceConnection.ID}`);
    return this.http.put(url, {}, this.startupService.getHttpOptions())
      .pipe(
        map(changedEcommerceConnection => {
          const m = new EcommerceConnection(ecommerceConnection);
          return m;
        }),
        catchError(this.handleErrorT<any>(method))
      );
  }

  sendProducts(ecommerceConnection: EcommerceConnection): Observable<EcommerceConnection> {
    const method = 'sendproducts';
    const url = `${environment.apiBaseURL}${this.getURLEndPoint()}/${method}/${ecommerceConnection.ID}`;
    this.messageService.add(`${this.getLoggingObjectTypeName()} ${method}: ecommerceConnectionId=${ecommerceConnection.ID}`);
    return this.http.put(url, {}, this.startupService.getHttpOptions())
      .pipe(
        map(changedEcommerceConnection => {
          const m = new EcommerceConnection(ecommerceConnection);
          return m;
        }),
        catchError(this.handleErrorT<any>(method))
      );
  }

  sendShipmentStatus(ecommerceConnection: EcommerceConnection): Observable<EcommerceConnection> {
    const method = 'sendshipmentstatus';
    const url = `${environment.apiBaseURL}${this.getURLEndPoint()}/${method}/${ecommerceConnection.ID}`;
    this.messageService.add(`${this.getLoggingObjectTypeName()} ${method}: ecommerceConnectionId=${ecommerceConnection.ID}`);
    return this.http.put(url, {}, this.startupService.getHttpOptions())
      .pipe(
        map(changedEcommerceConnection => {
          const m = new EcommerceConnection(ecommerceConnection);
          return m;
        }),
        catchError(this.handleErrorT<any>(method))
      );
  }

  /**
   * Starts the Shopify OAuth install flow for the current
   * organization. Server validates the selected role, signs a
   * `state` token, and returns the Shopify-hosted `authorizeUrl`
   * the SPA should redirect the browser to.
   *
   * Mirrors `POST /1.0/ui/ecommerceconnection/shopify/start`
   * (see `docs/contracts/shared/ShopifyAppInstall.md`).
   *
   * Errors propagate so the caller can render them inline — a
   * silent failure here would strand the merchant on the connect
   * screen with no feedback. `handleErrorT` is deliberately NOT
   * used.
   */
  shopifyInstallStart(payload: ShopifyInstallStartRequest): Observable<ShopifyInstallStartResponse> {
    const url = `${environment.apiBaseURL}${this.getURLEndPoint()}/shopify/start`;
    this.messageService.add(`${this.getLoggingObjectTypeName()} shopifyInstallStart: shop=${payload && payload.shop}`);
    return this.http.post<ShopifyInstallStartResponse>(url, payload, this.startupService.getHttpOptions());
  }

  /**
   * Exchanges the Shopify callback parameters for an active
   * `SHOPIFY` `EcommerceConnection` on the current organization.
   *
   * Mirrors `POST /1.0/ui/ecommerceconnection/shopify/finalize`
   * (see `docs/contracts/shared/ShopifyAppInstall.md`).
   *
   * 🛑 **DO NOT DELETE — hold requested by server 2026-10-14.**
   * This is dead code on the current install path, and an earlier
   * revision of `docs/architecture/shopify-authentication.md`
   * listed it for removal. The server has asked that it stay until
   * `POST /shopify/session` (token exchange) is deployed, because
   * it is the only remaining fallback if token exchange hits a
   * snag — removing it now would leave no install path at all.
   * See `docs/contracts/inbox/_processed/2026-10-14-shopify-managed-install-server-position.md`
   * §7 and `docs/architecture/shopify-authentication.md` §12.4
   * item 10.
   *
   * **Status:** Retained as a compatibility fallback. The
   * preferred flow is now `shopifyInstallStart` → browser
   * redirect to Shopify → server-side `GET /shopify` callback
   * → server redirects browser to the SPA `returnTo` with
   * `installed=1` and `ecommerceConnectionId` query params.
   * Only invoke `shopifyInstallFinalize` when the SPA is
   * entered with the raw Shopify callback params (`shop`,
   * `code`, `hmac`, `timestamp`) — i.e. the legacy direct-to-SPA
   * callback path.
   *
   * Errors propagate so the caller can render them inline; this
   * call deliberately does NOT use `handleErrorT` because a
   * silent failure would strand the user with an unconnected
   * Shopify store.
   */
  shopifyInstallFinalize(payload: ShopifyInstallFinalizeRequest): Observable<ShopifyInstallFinalizeResponse> {
    const url = `${environment.apiBaseURL}${this.getURLEndPoint()}/shopify/finalize`;
    this.messageService.add(`${this.getLoggingObjectTypeName()} shopifyInstallFinalize: shop=${payload && payload.shop}`);
    return this.http.post<ShopifyInstallFinalizeResponse>(url, payload, this.startupService.getHttpOptions());
  }

  /**
   * Redeem a Shopify install nonce, binding the shop to the selected
   * organization and upserting the ACTIVE `SHOPIFY` EcommerceConnection.
   *
   * **This is the only call in the entire system that creates a Shopify
   * EcommerceConnection.** Completing the install does not, opening the
   * embedded app does not, `POST /shopify/session` does not (it only parks
   * the offline token against a nonce), and creating a Snowfall Press account
   * does not. See `docs/contracts/shared/ShopifyAppInstall.md`.
   *
   * **Never send `shop`.** The server takes it from the stored install row,
   * which came from a Shopify-validated session token. A client-supplied shop
   * would let any authenticated user claim any store.
   *
   * Requires a selected `organization` role; every other role is refused with
   * 403. Errors propagate rather than being swallowed by `handleErrorT` --
   * each status means something different to the merchant and a silent
   * failure would strand them with an installed app and nothing connected.
   */
  shopifyLink(payload: ShopifyLinkRequest): Observable<ShopifyLinkResponse> {
    const url = `${environment.apiBaseURL}${this.getURLEndPoint()}/shopify/link`;
    // The nonce is deliberately absent from this line: it is a bearer
    // credential for a store and must not reach a log.
    this.messageService.add(`${this.getLoggingObjectTypeName()} shopifyLink: redeeming install link`);
    return this.http.post<ShopifyLinkResponse>(url, payload, this.startupService.getHttpOptions());
  }

  private handleErrorT<T> (operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      this.loadingState = 'INITIAL';

      // TODO: send the error to remote logging infrastructure
      console.error(error, operation); // log to console instead

      // TODO: better job of transforming error for user consumption
      this.log(`${operation} failed: ${error.message}`);

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }
}

/**
 * Request body for `POST {apiBaseURL}ecommerceconnection/shopify/start`.
 * The SPA initiates the OAuth flow from inside the authenticated
 * application; the server validates the selected `organization`
 * role, signs the `state`, and hands back a Shopify-hosted
 * `authorizeUrl` for the browser to follow.
 */
export interface ShopifyInstallStartRequest {
  /** Either the bare shop handle (`my-store`) or full domain (`my-store.myshopify.com`). */
  shop: string;
  /** Optional Shopify `host` param when arriving from an embedded launch. */
  host?: string;
  /**
   * Optional SPA URL the server should redirect the browser back
   * to after callback finalization. The server appends `shop`,
   * `host` (when known), `installed=1`, and `ecommerceConnectionId`
   * query params on return.
   */
  returnTo?: string;
}

/**
 * Response body from `POST {apiBaseURL}ecommerceconnection/shopify/start`.
 * See `docs/contracts/shared/ShopifyAppInstall.md`.
 */
export interface ShopifyInstallStartResponse {
  /** Normalized shop domain (e.g. `my-store.myshopify.com`). */
  shop: string;
  /** Signed org/install context token the server validates on callback. */
  state: string;
  /** Registered Shopify callback URL the server has on file (currently `/shopify`). */
  redirectUri: string;
  /** Shopify-hosted authorization URL — the browser navigates here next. */
  authorizeUrl: string;
}

/**
 * Request body for `POST {apiBaseURL}ecommerceconnection/shopify/finalize`.
 * Mirrors the Shopify install callback query string captured from
 * the SPA entry URL.
 *
 * Used only by the legacy direct-callback fallback path. The
 * preferred flow uses `ShopifyInstallStartRequest` →
 * server-side `GET /shopify` and never lands the SPA with these
 * raw callback params.
 */
export interface ShopifyInstallFinalizeRequest {
  shop: string;
  code: string;
  hmac: string;
  timestamp: number;
  state?: string;
  host?: string;
}

/**
 * Request body for `POST {apiBaseURL}ecommerceconnection/shopify/link`.
 *
 * One field, and that is the whole point: the shop is resolved server-side
 * from the stored install row rather than being asserted by the client.
 */
export interface ShopifyLinkRequest {
  /** Single-use, ten-minute TTL. Minted by `POST /shopify/session`. */
  nonce: string;
}

/**
 * Response body from `POST {apiBaseURL}ecommerceconnection/shopify/link`.
 * See `docs/contracts/shared/ShopifyAppInstall.md`.
 */
export interface ShopifyLinkResponse {
  ecommerceConnectionId: number;
  /** Full myshopify domain, taken from the stored row -- not from the client. */
  shop: string;
  orgId: number;
  /** `false` on a re-link of a connection that already existed. */
  created: boolean;
  /** EventBridge setup outcome: `OK` | `SKIPPED` | ... */
  setupStatus: string;
  /** Non-fatal. Surface these; do NOT treat them as a failure. */
  warnings: string[];
}

/**
 * Response body from `POST {apiBaseURL}ecommerceconnection/shopify/finalize`.
 * See `docs/contracts/shared/ShopifyAppInstall.md`.
 */
export interface ShopifyInstallFinalizeResponse {
  ecommerceConnectionId: number;
  remoteURL: string;
  created: boolean;
  setupStatus: string;
  warnings: string[];
}

